import { html, nothing, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BaseComponent } from "../base-component/base-component";
import { DownloadResourceConfig, DownloadResourceStatus } from "./interfaces/mhp-interface";
import { StoreState } from "../../store/store.interface";
import { getUserDataForUserAnalytics, isHeStudent, isSuccessUser } from "../user-profile/user-profile.selectors";
import { connect } from "pwa-helpers";
import store from "../../store/store";
import { StudentInstructorToggleGroup } from "../../common/utils/analytics-util";
import { REDIRECT_URL_HECLR_KEY } from "../../common/constants/api-constants";
import { instructorModalPrimaryBtn, instructorModalSecondaryBtn, showConfirmInstructorModal } from "../pdp/utils";
import environment from "../../common/environment";
import emit from "../../common/events";
import { PROFESSIONAL } from "../../common/utils/util";
import { booleanConverter } from "../../common/converters";

const INSTRUCTOR_COPY = "Locked resources are reserved for instructors only.";
const ACCESS_COPY = "To access these materials,";
const SIGN_IN = "sign in";
const CREATE_ACCOUNT = "create account";

@customElement('mhecomm-mhp-downloads-resources')
export class MhpDownloadsResources extends connect(store)(BaseComponent) {

  /**
   * A JSON String in the format of Array<CatalogConfig>.
   */
  @property({ type: Array })
  resources: Array<DownloadResourceConfig> = [];

  @property()
  isbn?: String

  @property({ converter: booleanConverter })
  isOutOfStock = false;

  /* A flag to determine if the user is logged in or not.
  */
  @state()
  private isLoggedInUser = false;

  /* A flag to determine if For Instructors view toggle button is active.
  */
  @state()
  private isInstructorView = false;

  private freeResources: Array<DownloadResourceConfig> = [];

  private instructorResources: Array<DownloadResourceConfig> = [];

  stateChanged(state: StoreState) {
    this.isLoggedInUser = isSuccessUser(state);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.handleDocumentClick);
  }

  updated() {
    const accordionEl = document.querySelector('.accordion-item.mhp_downloads_resources');
    const state = store.getState() as StoreState;
    const isFree = accordionEl?.classList.contains('free');
    const isInstr = accordionEl?.classList.contains('instructor');
    const isMixed = accordionEl?.classList.contains('mixed');
    const guestStudentView = !this.isInstructorView && isInstr;
    const guestInstructorView = this.isInstructorView && isFree;
    const loggedInStudentView = isHeStudent(state) && this.isInstructorView && (isInstr || isMixed);
    
    if (guestStudentView || guestInstructorView || loggedInStudentView) {
      accordionEl?.classList.add('d-none');
      accordionEl?.setAttribute('aria-hidden', 'true');
    } else {
      accordionEl?.classList.remove('d-none');
      accordionEl?.setAttribute('aria-hidden', 'false');
    }

  }

  protected async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    this.freeResources = this.resources?.length > 0 ? this.resources.filter((item: DownloadResourceConfig) => item.status === DownloadResourceStatus.FREE) : [];
    this.instructorResources = this.resources?.length > 0 ? this.resources.filter((item: DownloadResourceConfig) => item.status === DownloadResourceStatus.INSTRUCTOR) : [];
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleDocumentClick);
  }

  renderComp(): TemplateResult {
    return html`
      <div class="mhp-downloads-resources-accordion-section">

        <div class="mhp-downloads-resources-support-email-login">
          <p>
            ${!this.isLoggedInUser && this.isInstructorView && this.instructorResources?.length > 0
              ? html`<span>${INSTRUCTOR_COPY}</span>`
              : nothing
            }
            ${!this.isLoggedInUser
              ? this.renderLoginOrCreateAccountLink()
              : nothing
            }
          </p>
        </div>

        ${this.resources?.length > 0 
          ? this.renderResourcesList(this.isInstructorView ? this.instructorResources : this.freeResources )
          : nothing
        }

      </div>
    `;
  }

  /**
   * Render login or create account links
   */
  private renderLoginOrCreateAccountLink(): TemplateResult {
    return html`
      <span> 
        ${ACCESS_COPY} 
        ${this.getLoginOrCreateLink(true)} 
          or 
        ${this.getLoginOrCreateLink(false)}. 
    </span>`;
  }

  /**
   * Login / Create Account Link
   * @returns anchor element with attributes
   */
  private getLoginOrCreateLink(isLoginLink: boolean): TemplateResult {
    const label = isLoginLink ? SIGN_IN : CREATE_ACCOUNT;
    return html`
      <a 
        href=${this.isInstructorView ? "#" : this.redirectToLoginOrCreateAccountUrl()}
        id="mhp-${isLoginLink ? 'sign-in' : 'create-account'}"
        data-automation-id="mhp-downloads-resources-${isLoginLink ? 'sign-in' : 'create-account'}-link"
        aria-label="${isLoginLink ? 'Sign In' : 'Create Account'}"
        @click=${(e: Event) => (this.handleLinkClick(e, {name: label, isDownloadLink: false}))}
      >
        ${label} 
      </a>
    `;
  }

  /**
   * Render resources list items
   */
  private renderResourcesList(resourcesList: DownloadResourceConfig[]): TemplateResult {
    return html`
      <ul id="mhp-downloads-resource-${this.isInstructorView ? 'instructor' : 'student'}-items" class="mhp-downloads-resources-list list-unstyled w-fit-content">
        ${resourcesList.map(
          (resourceItem: DownloadResourceConfig, index) =>
            html` 
              <li id="mhp-downloads-resources-${this.isInstructorView ? 'instructor' : 'student'}-item-${index}" class="d-flex gap-2 mb-3">
                ${this.renderResourceLink(resourceItem, index)}
                ${this.renderLockIcon(resourceItem)}
              </li>
            `
        )}
      </ul>
    `;
  }

  /**
   * Render resource link 
  */
  private renderResourceLink(item: DownloadResourceConfig, n: Number): TemplateResult {
    const guestUserlabel = `${this.isInstructorView ? 'Locked' : ''} ${item.title}`.trim();
    const loggedInUserlabel = `Download ${item.title}`.trim();
    if (!this.isLoggedInUser) {
      return html `
        <a 
          data-automation-id="mhp-downloads-resources-${this.isInstructorView ? 'instructor' : 'student'}-item-${n}"
          href=${this.isInstructorView ? "#" :this.redirectToLoginOrCreateAccountUrl()}
          aria-label="Please sign in to download resource"
          @click=${(e: Event) => (this.handleLinkClick(e, {name: guestUserlabel, isDownloadLink: true}))}
        >
          ${item.title}
        </a> 
      `;
    }

    return html`
      <a 
        data-automation-id="mhp-downloads-resources-${this.isInstructorView ? 'instructor' : 'student'}-item-${n}"
        href="${item.url}" 
        aria-label="Download resource"
        target="_blank"
        @click=${() => this.fireAnalytics({label: loggedInUserlabel, isDownloadLink: true})}
      >
        ${item.title}
      </a> 
    `;
  }

  /**
  * Render lock icon 
  */
  private renderLockIcon(item: DownloadResourceConfig) {
    return html`
      ${ !this.isLoggedInUser && this.isInstructorView && (item.status === DownloadResourceStatus.INSTRUCTOR)
        ? this.uxtIconReady ? html`<uxt-icon icon="lock-fill" size="md"></uxt-icon>` : ''
        : nothing }
    `;
  }

  private handleDocumentClick = (e: MouseEvent) => {
    const studentInstructorToggleBtn = (e.target as HTMLElement).closest('[data-layer-comp="studentInstructorToggleGroup"]');

    if (studentInstructorToggleBtn) {
      const viewType = (studentInstructorToggleBtn as HTMLElement).dataset.layerPdpViewType;
      if (viewType === StudentInstructorToggleGroup.INSTRUCTOR) {
        this.isInstructorView = true;
      } else if (viewType === StudentInstructorToggleGroup.STUDENT) {
        this.isInstructorView = false;
      }
    }

  };

  private redirectToLoginOrCreateAccountUrl(isLinkClick?: boolean) {
    const redirectUrl = new URL(environment().baseLoginUrl);
    redirectUrl.searchParams.set('flow', 'mhp_pdp');
    redirectUrl.searchParams.set('userType', this.isInstructorView ? 'instructor' : 'student');
    redirectUrl.searchParams.set(REDIRECT_URL_HECLR_KEY, window.location.href);

    if (isLinkClick) {
      window.location.href = redirectUrl.toString();
      return;
    } 

    return redirectUrl.toString();
  }

  private handleLinkClick(
    e: Event, 
    { 
      name, 
      isDownloadLink
    }:{
      name: String,
      isDownloadLink: Boolean
    }) {
    this.fireAnalytics({label: name, isDownloadLink});
    if (this.isInstructorView) {
      this.handleInstructorLoginOrCreateAccountClick(e);
    }
  }

  private async handleInstructorLoginOrCreateAccountClick(e: Event) {
    if (!this.isLoggedInUser && this.isInstructorView) {
      if (!(await showConfirmInstructorModal(e))) { 
        this.fireAnalytics({label: instructorModalSecondaryBtn, isDownloadLink: false});
        return;
      }
    }
    this.fireAnalytics({label: instructorModalPrimaryBtn, isDownloadLink: false});
    this.redirectToLoginOrCreateAccountUrl(true);
  }

  /**
  * Emits an event when the link is clicked.
  */
  private fireAnalytics(
    {
      label, 
      isDownloadLink
    }:{
      label: String,
      isDownloadLink: Boolean
    }): void {
    const state = store.getState() as StoreState;

    emit({
      element: this,
      name: 'click',
      detail: {
        businessUnit: PROFESSIONAL.toLowerCase(),
        ...(isDownloadLink && { 
          sampleId: this.isbn, 
          isOutOfStock: this.isOutOfStock, 
          id: this.isInstructorView ? 'Instructor Sample DL' : 'Student Sample DL'
        }),
        comp: 'mhecomm-mhp-downloads-resources',
        eventName: !isDownloadLink 
          ? 'mhp-downloads-resources-login-create-account-link-clicked' 
          : this.isInstructorView 
            ? 'Instructor Sample DL' 
            : 'Student Sample DL',
        label,
        tab: this.isInstructorView ? StudentInstructorToggleGroup.INSTRUCTOR : StudentInstructorToggleGroup.STUDENT,
        user: getUserDataForUserAnalytics(state)
      },
      dataLayer: true
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-mhp-downloads-resources': MhpDownloadsResources;
  }
}
    